<template>
  <v-container fluid>
    <v-card class="elevation-2 rounded-lg overflow-hidden">
      <!-- En-tête avec dégradé -->
      <div class="logs-header px-4 py-3">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-console</v-icon>
          <h2 class="text-h5 font-weight-bold white--text mb-0">Système de Logs Serveur</h2>
          <v-spacer></v-spacer>
          <v-btn
              color="white"
              small
              text
              @click="refreshData"
              :loading="refreshing"
              :disabled="loading"
              class="mr-2"
          >
            <v-icon left size="18">mdi-refresh</v-icon>
            Actualiser
          </v-btn>
        </div>
      </div>

      <!-- Indicateur de chargement -->
      <v-progress-linear
          :active="loading"
          :indeterminate="loading"
          color="primary"
          height="3"
      ></v-progress-linear>

      <!-- Statistiques des logs -->
      <v-card-text class="py-3 px-4">
        <v-row class="mb-3">
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="1" class="pa-3 rounded-lg">
              <div class="d-flex align-center">
                <v-avatar color="error lighten-4" size="42" class="mr-3">
                  <v-icon color="error">mdi-alert-circle</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Erreurs</div>
                  <div class="text-h6 font-weight-bold">{{ errorCount }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="1" class="pa-3 rounded-lg">
              <div class="d-flex align-center">
                <v-avatar color="warning lighten-4" size="42" class="mr-3">
                  <v-icon color="warning">mdi-alert</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Avertissements</div>
                  <div class="text-h6 font-weight-bold">{{ warningCount }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="1" class="pa-3 rounded-lg">
              <div class="d-flex align-center">
                <v-avatar color="info lighten-4" size="42" class="mr-3">
                  <v-icon color="info">mdi-information</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Informations</div>
                  <div class="text-h6 font-weight-bold">{{ infoCount }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="1" class="pa-3 rounded-lg">
              <div class="d-flex align-center">
                <v-avatar color="success lighten-4" size="42" class="mr-3">
                  <v-icon color="success">mdi-check-circle</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Total</div>
                  <div class="text-h6 font-weight-bold">{{ totalCount }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filtres -->
        <v-row>
          <v-col cols="12" sm="4">
            <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Rechercher..."
                clearable
                hide-details
                outlined
                dense
                class="rounded-lg"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
                v-model="typeFilter"
                :items="typeOptions"
                label="Type de log"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-filter-variant"
                clearable
                class="rounded-lg"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-menu
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="dateRangeText"
                    label="Période"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    hide-details
                    class="rounded-lg"
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="dates"
                  range
                  no-title
                  color="primary"
                  @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="2" class="text-right">
            <v-btn
                color="primary"
                class="elevation-1"
                :disabled="!serverlogs.length"
                @click="downloadCSV"
            >
              <v-icon left>mdi-download</v-icon>
              Exporter CSV
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Tableau des logs -->
      <v-data-table
          :headers="headers"
          :items="filteredLogs"
          :search="search"
          :loading="loading"
          :items-per-page.sync="itemsPerPage"
          :items-per-page-options="[10, 15, 25, 50]"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          :footer-props="{
          'items-per-page-text': 'Lignes par page',
          'items-per-page-options': [10, 15, 25, 50],
          'show-first-last-page': true,
          'show-current-page': true,
          'page-text': '{0}-{1} sur {2}'
        }"
          show-expand
          @click:row="expandRow"
          class="logs-table elevation-0"
      >
        <!-- Formatage de la date -->
        <template v-slot:[`item.dateLog`]="{ item }">
          <div class="d-flex flex-column">
            <span class="text-body-2 font-weight-medium">{{ formatDateOnly(item.dateLog) }}</span>
            <span class="text-caption grey--text">{{ formatTimeOnly(item.dateLog) }}</span>
          </div>
        </template>

        <!-- Type de log avec badge coloré -->
        <template v-slot:[`item.typeLog`]="{ item }">
          <v-chip
              small
              :color="getTypeColor(item.typeLog)"
              text-color="white"
              label
          >
            {{ item.typeLog }}
          </v-chip>
        </template>

        <!-- Classe invoquée avec troncature et tooltip -->
        <template v-slot:[`item.fileRunned`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div class="text-truncate" style="max-width: 250px;" v-bind="attrs" v-on="on">
                <v-icon small class="mr-1 grey--text">mdi-file-code-outline</v-icon>
                {{ item.fileRunned }}
              </div>
            </template>
            <span>{{ item.fileRunned }}</span>
          </v-tooltip>
        </template>

        <!-- Requête avec méthode mise en évidence -->
        <template v-slot:[`item.requestType`]="{ item }">
          <div>
            <v-chip
                x-small
                outlined
                :color="getRequestColor(item.requestType)"
                class="mr-1"
            >
              {{ item.requestType }}
            </v-chip>
          </div>
        </template>

        <!-- Détails étendus -->
        <template v-slot:[`expanded-item`]="{ headers, item }">
          <td :colspan="headers.length" class="pa-4">
            <v-card flat class="expanded-details pa-3 rounded-lg grey lighten-5">
              <div class="d-flex mb-2">
                <v-chip :color="getTypeColor(item.typeLog)" label small class="mr-2" dark>{{ item.typeLog }}</v-chip>
                <v-spacer></v-spacer>
                <v-btn x-small text color="grey" @click="copyToClipboard(item)">
                  <v-icon small left>mdi-content-copy</v-icon>
                  Copier
                </v-btn>
              </div>
              <vue-json-pretty
                  :data="item"
                  :deep="2"
                  highlight-mouseover
                  :showDoubleQuotes="true"
                  :showLength="true"
              ></vue-json-pretty>
            </v-card>
          </td>
        </template>

        <!-- État vide -->
        <template v-slot:[`no-data`]>
          <v-card-text class="text-center py-6">
            <v-icon size="64" color="grey lighten-1">mdi-text-box-outline</v-icon>
            <div class="text-h6 grey--text text--darken-1 mt-2">Aucune donnée disponible</div>
            <div class="text-body-2 grey--text">Aucun log serveur n'a été trouvé</div>
          </v-card-text>
        </template>
      </v-data-table>
    </v-card>

    <!-- Snackbar pour les notifications -->
    <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="3000"
        bottom
        right
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from "../../config/api.js";
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

function isMobileDevice() {
  return /Mobi|Android/i.test(navigator.userAgent);
}

export default {
  data() {
    return {
      isMobile: isMobileDevice(),
      search: "",
      loading: true,
      refreshing: false,
      serverlogs: [],
      filteredLogs: [],
      headers: [
        {text: "Date de création", value: "dateLog", width: "180px"},
        {text: "Type", value: "typeLog", width: "120px"},
        {text: "Classe invoquée", value: "fileRunned"},
        {text: "Requête", value: "requestType", width: "150px"}
      ],
      expanded: [],
      singleExpand: true,
      itemsPerPage: 15,

      // Nouveaux champs pour les filtres
      dateMenu: false,
      dates: [],
      typeFilter: null,
      typeOptions: [
        {text: "ERROR", value: "ERROR"},
        {text: "WARN", value: "WARN"},
        {text: "INFO", value: "INFO"},
        {text: "DEBUG", value: "DEBUG"}
      ],

      // Notifications
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success'
    };
  },
  computed: {
    dateRangeText() {
      if (!this.dates || this.dates.length === 0) return 'Toutes les dates';
      if (this.dates.length === 1) return format(parseISO(this.dates[0]), 'dd/MM/yyyy');
      if (this.dates.length === 2) {
        return `${format(parseISO(this.dates[0]), 'dd/MM/yyyy')} - ${format(parseISO(this.dates[1]), 'dd/MM/yyyy')}`;
      }
      return 'Période sélectionnée';
    },

    errorCount() {
      return this.serverlogs.filter(log => log.typeLog === "ERROR").length;
    },

    warningCount() {
      return this.serverlogs.filter(log => log.typeLog === "WARN").length;
    },

    infoCount() {
      return this.serverlogs.filter(log =>
          log.typeLog === "INFO" || log.typeLog === "DEBUG"
      ).length;
    },

    totalCount() {
      return this.serverlogs.length;
    }
  },
  watch: {
    dates: {
      handler() {
        this.applyFilters();
      },
      deep: true
    },
    typeFilter() {
      this.applyFilters();
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true;
      apiClient
          .get(`${this.$store.getters.user.baseUrl}/logs/all`)
          .then(response => {
            this.serverlogs = response.data.datas;
            this.filteredLogs = [...this.serverlogs];
            this.loading = false;
          })
          .catch(error => {
            console.log("serverlogs error:", error);
            this.loading = false;
            this.showSnackbar('Erreur lors du chargement des logs', 'error');
          });
    },
    refreshData() {
      this.refreshing = true;
      apiClient
          .get(`${this.$store.getters.user.baseUrl}/logs/all`)
          .then(response => {
            this.serverlogs = response.data.datas;
            this.applyFilters();
            this.refreshing = false;
            this.showSnackbar('Logs actualisés avec succès', 'success');
          })
          .catch(error => {
            console.log("refresh error:", error);
            this.refreshing = false;
            this.showSnackbar('Erreur lors de l\'actualisation des logs', 'error');
          });
    },
    formatDateOnly(dateString) {
      if (!dateString) return 'N/A';
      try {
        return format(new Date(dateString), 'dd MMM yyyy', {locale: fr});
      } catch (e) {
        return dateString;
      }
    },
    formatTimeOnly(dateString) {
      if (!dateString) return '';
      try {
        return format(new Date(dateString), 'HH:mm:ss');
      } catch (e) {
        return '';
      }
    },
    expandRow(item, {isExpanded}) {
      if (isExpanded) return;
      this.expanded = [item];
    },
    getTypeColor(type) {
      const colors = {
        ERROR: "error",
        WARN: "warning",
        INFO: "info",
        DEBUG: "primary"
      };
      return colors[type] || "grey";
    },
    getRequestColor(requestType) {
      const colors = {
        GET: "primary",
        POST: "success",
        PUT: "warning",
        DELETE: "error",
        PATCH: "orange"
      };
      return colors[requestType] || "grey";
    },
    downloadCSV() {
      const csvContent = this.convertToCSV(this.filteredLogs);
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `server-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showSnackbar('Fichier CSV téléchargé', 'success');
    },
    convertToCSV(data) {
      const headers = ['Date', 'Type', 'Classe', 'Requête'];
      let csvRows = [];

      csvRows.push(headers.join(','));

      for (const log of data) {
        const row = [
          log.dateLog,
          log.typeLog,
          log.fileRunned || '',
          log.requestType || ''
        ];

        csvRows.push(row.map(value => `"${value}"`).join(','));
      }

      return csvRows.join('\n');
    },
    applyFilters() {
      // Filtrer par type de log si sélectionné
      let filtered = this.serverlogs;

      if (this.typeFilter) {
        filtered = filtered.filter(log => log.typeLog === this.typeFilter);
      }

      // Filtrer par dates si sélectionnées
      if (this.dates && this.dates.length > 0) {
        if (this.dates.length === 1) {
          // Un seul jour sélectionné
          const selectedDate = new Date(this.dates[0]);
          selectedDate.setHours(0, 0, 0, 0);
          const nextDay = new Date(selectedDate);
          nextDay.setDate(nextDay.getDate() + 1);

          filtered = filtered.filter(log => {
            const logDate = new Date(log.dateLog);
            return logDate >= selectedDate && logDate < nextDay;
          });
        } else if (this.dates.length === 2) {
          // Période sélectionnée
          const startDate = new Date(this.dates[0]);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(this.dates[1]);
          endDate.setHours(23, 59, 59, 999);

          filtered = filtered.filter(log => {
            const logDate = new Date(log.dateLog);
            return logDate >= startDate && logDate <= endDate;
          });
        }
      }

      this.filteredLogs = filtered;
    },
    copyToClipboard(item) {
      try {
        navigator.clipboard.writeText(JSON.stringify(item, null, 2));
        this.showSnackbar('Copié dans le presse-papier', 'success');
      } catch (err) {
        this.showSnackbar('Erreur lors de la copie', 'error');
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    dynamicID() {
      return 'dynamicID-' + Math.floor(Math.random() * Date.now().toString());
    }
  }
};
</script>

<style scoped>
.logs-header {
  background: linear-gradient(to right,  #01579B, #1e88e5);
}

.expanded-details {
  border-radius: 4px;
  overflow: auto;
  max-height: 400px;
}

.logs-table ::v-deep th {
  font-weight: bold !important;
  background-color: #f5f5f5 !important;
}

.logs-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

.logs-table ::v-deep tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
}

.logs-table ::v-deep tbody tr:nth-of-type(even) {
  background-color: rgba(240, 240, 240, 0.5);
}

.logs-table ::v-deep tbody tr:nth-of-type(odd) {
  background-color: rgb(250, 250, 250);
}

.logs-table ::v-deep tbody tr.v-data-table__expanded__content {
  box-shadow: inset 0 4px 4px -4px rgba(0, 0, 0, 0.1);
}
</style>